import './kpi-tile.css';

import { ContentItem } from '@kerfjs/ui/content-item';
import { px } from '@kerfjs/ui/css-values';
import type { TextContent } from '@kerfjs/ui/text';
import { type SafeHtml } from 'kerfjs';

import { currencyLocale } from '../lib/currency-format.js';

/** The narrowest a KPI tile gets; longer values scale down inside this width (HS2-S64NKZ). */
export const KPI_TILE_MIN_WIDTH = px(212);

type KpiTileProps =
  | { value: TextContent; label: string; amount?: never; currency?: never }
  | { amount: number | null; currency: string; label: string; value?: never };

/** A monospace character takes about 0.6em. Leave a small margin for font and browser differences. */
export function kpiValueFit(characters: number): number {
  return 1 / (Math.max(1, characters) * 0.66);
}

export function KpiTile(props: KpiTileProps): SafeHtml {
  const { label } = props;
  const monetary = props.currency !== undefined;
  const amount = monetary ? (props.amount ?? null) : null;
  const locale = currencyLocale();
  const numberText =
    monetary && amount !== null && Number.isFinite(amount)
      ? new Intl.NumberFormat(locale, { minimumFractionDigits: 0, maximumFractionDigits: 4 }).format(amount)
      : '—';
  const characters = monetary ? numberText.length : typeof props.value === 'string' ? props.value.length : 6;
  return (
    <ContentItem className="kpi-tile" frame="framed" appearance="surface">
      <div class="kpi-tile-content">
        <span class="kpi-tile-value">
          {monetary ? <span class="kpi-tile-unit">{props.currency}</span> : null}
          <span class="kpi-tile-number" style={`--kpi-value-fit: ${String(kpiValueFit(characters))}`}>
            {monetary ? (amount === null || !Number.isFinite(amount) ? '—' : numberText) : props.value}
          </span>
        </span>
        <span class="kpi-tile-label">{label}</span>
      </div>
    </ContentItem>
  );
}
